import { useEffect, useState, Fragment } from "react";
import ModalFrame from "../../components/modal/ModalFrame";
import { Dialog, Switch } from "@headlessui/react";
import {
  PlusIcon,
  DocumentIcon,
  ArrowsExpandIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import * as api from "./apiService";
import { toast } from "react-toastify";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const AddTailAlertModal = ({ isOpen, handleClose, handleAddTailDetails }) => {
  const [tailNumber, setTailNumber] = useState("");
  const [message, setMessage] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);

  // propagation
  const [propagationAircraftType, setPropagationAircraftType] = useState(null);
  const [propagationCustomer, setPropagationCustomer] = useState(null);
  const [isPropagated, setIsPropagated] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  // fetch lookups as the user types a tail
  useEffect(() => {
    const timeoutID = setTimeout(() => {
      getTailLookups();
    }, 250);
    return () => clearTimeout(timeoutID);
  }, [tailNumber]);

  const getTailLookups = async () => {
    if ((tailNumber || "").trim().length > 2) {
      try {
        const { data } = await api.getTailAircraftLookup(tailNumber.trim());
        if (data) {
          setPropagationAircraftType({
            id: data.aircraft_id,
            name: data.aircraft_name,
          });
          setPropagationCustomer({
            id: data.customer_id,
            name: data.customer_name,
          });
        } else {
          setPropagationAircraftType(null);
          setPropagationCustomer(null);
          setIsPropagated(false);
        }
      } catch {
        setPropagationAircraftType(null);
        setPropagationCustomer(null);
        setIsPropagated(false);
      }
    } else {
      setPropagationAircraftType(null);
      setPropagationCustomer(null);
      setIsPropagated(false);
    }
  };

  const addTailDetails = async () => {
    // basic validation
    if (!tailNumber.trim() || (!message.trim() && !notes.trim())) {
      toast.error("Please enter a tail number and at least an Alert or Notes.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      if (selectedFile) formData.append("file", selectedFile);
      formData.append("isPublic", String(isPublic));
      formData.append("tailNumber", tailNumber.trim());
      formData.append("message", message.trim());
      formData.append("notes", notes.trim());

      // append propagation flags/ids only if enabled
      formData.append("is_propagated", String(isPropagated));
      if (isPropagated && propagationCustomer?.id) {
        formData.append(
          "propagation_customer_id",
          String(propagationCustomer.id)
        );
      }
      if (isPropagated && propagationAircraftType?.id) {
        formData.append(
          "propagation_aircraft_type_id",
          String(propagationAircraftType.id)
        );
      }

      const { data } = await api.addTailAlert(formData);
      toast.success("Tail alert created.");
      handleAddTailDetails(data);
    } catch (error) {
      toast.error("Unable to add tail details.");
      setErrorMessage(error.response.data.error);
      setLoading(false);
    }
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting same filename
    setSelectedFile(file || null);
  };

  const removeSelectedFile = () => setSelectedFile(null);

  const canPropagate = Boolean(propagationAircraftType && propagationCustomer);

  return (
    <ModalFrame isModalOpen={isOpen}>
      {/* Header */}
      <div className="mb-6" style={{ minWidth: "320px" }}>
        <Dialog.Title
          as="h3"
          className="text-xl font-semibold leading-6 text-gray-900"
        >
          Add Tail Alert
        </Dialog.Title>
        <p className="mt-1 text-sm text-gray-500">
          Create an alert and optional notes for this tail. You can attach a
          file and choose whether to make it visible to customers.
        </p>
      </div>

      {/* Tail number */}
      <div>
        <label
          htmlFor="tailNumber"
          className="block text-sm font-medium text-gray-700"
        >
          Tail Number <span className="text-red-500">*</span>
        </label>
        <div className="mt-1">
          <input
            type="text"
            value={tailNumber}
            onChange={(e) => setTailNumber(e.target.value.toUpperCase())}
            name="tailNumber"
            id="tailNumber"
            placeholder="e.g., N123AB"
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Propagation block */}
      {canPropagate && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 text-amber-900">
              <ArrowsExpandIcon className="h-8 w-8" />
              <div>
                <div className="text-sm font-semibold">
                  Apply to matching tails
                </div>
                <p className="text-sm text-amber-900/80">
                  We detected this tail belongs to{" "}
                  <span className="font-medium">
                    {propagationCustomer?.name}
                  </span>{" "}
                  and is a{" "}
                  <span className="font-medium">
                    {propagationAircraftType?.name}
                  </span>
                  . Enable the toggle to also apply this alert and notes to
                  other tails for this customer with the same aircraft type.
                </p>
              </div>
            </div>
            <Switch
              checked={isPropagated}
              onChange={setIsPropagated}
              className={classNames(
                isPropagated ? "bg-amber-500" : "bg-gray-200",
                "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-400"
              )}
            >
              <span
                aria-hidden="true"
                className={classNames(
                  isPropagated ? "translate-x-5" : "translate-x-0",
                  "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                )}
              />
            </Switch>
          </div>

          {isPropagated && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center rounded-md bg-white px-2 py-1 font-medium text-amber-900 ring-1 ring-amber-200">
                Customer: {propagationCustomer?.name}
              </span>
              <span className="inline-flex items-center rounded-md bg-white px-2 py-1 font-medium text-amber-900 ring-1 ring-amber-200">
                Aircraft Type: {propagationAircraftType?.name}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Alert */}
      <div className="mt-6">
        <label
          htmlFor="message"
          className="block text-sm font-medium text-gray-700"
        >
          Alert
        </label>
        <div className="mt-1">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            name="message"
            id="message"
            placeholder="What should users know at a glance? (e.g., Crew must check galley cabinet hinges before departure.)"
            style={{ minHeight: "100px" }}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Shown prominently in job creation and job details.
        </p>
      </div>

      {/* Notes */}
      <div className="mt-6">
        <label
          htmlFor="notes"
          className="block text-sm font-medium text-gray-700"
        >
          Notes
        </label>
        <div className="mt-1">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            name="notes"
            id="notes"
            placeholder="Optional context for internal teams (access instructions, security, parking notes, etc.)"
            style={{ minHeight: "100px" }}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Attachment */}
      <label
        htmlFor="file-upload"
        className="mt-6 block text-sm font-medium text-gray-700"
      >
        Attachment
      </label>
      <div className="mt-1 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-6">
        <div className="text-center">
          <DocumentIcon className="mx-auto h-12 w-12 text-gray-300" />
          <div className="mt-2 flex justify-center text-sm leading-6 text-gray-600">
            <label
              htmlFor="file-upload"
              className="relative cursor-pointer rounded-md bg-white px-2 py-1 font-semibold text-sky-600 hover:text-sky-700 focus-within:outline-none"
            >
              <span>Upload a file</span>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                className="sr-only"
                onChange={handleFileChange}
              />
            </label>
          </div>
          <p className="text-xs leading-5 text-gray-500">
            PDF, Word, Excel, TXT
          </p>
        </div>
      </div>

      {/* Selected file + public toggle */}
      {selectedFile && (
        <div className="mt-6">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-gray-900">
                Selected file
              </div>
              <div className="text-sm text-gray-700">{selectedFile.name}</div>
            </div>
            <button
              type="button"
              onClick={removeSelectedFile}
              className="rounded-full px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
              aria-label="Remove file"
            >
              Remove
            </button>
          </div>

          <div className="mt-4">
            <Switch.Group
              as="div"
              className="flex items-center justify-between"
            >
              <div className="flex flex-col">
                <Switch.Label
                  as="p"
                  className="text-sm font-medium text-gray-900"
                  passive
                >
                  Visible to Customers
                </Switch.Label>
                <Switch.Description className="text-sm text-gray-500">
                  Allow customer users to download this attachment.
                </Switch.Description>
              </div>
              <Switch
                checked={isPublic}
                onChange={setIsPublic}
                className={classNames(
                  isPublic ? "bg-sky-500" : "bg-gray-200",
                  "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-400"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    isPublic ? "translate-x-5" : "translate-x-0",
                    "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </Switch.Group>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="rounded-md bg-red-50 p-4 mt-2">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon
                className="h-5 w-5 text-red-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc space-y-1">{errorMessage}</ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-10 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          onClick={addTailDetails}
          disabled={loading}
          className={classNames(
            "inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm",
            loading
              ? "bg-sky-300"
              : "bg-red-600 hover:bg-red-700 focus:ring-red-500"
          )}
        >
          {loading ? (
            "Adding…"
          ) : (
            <>
              <PlusIcon
                className="-ml-2 mr-1 h-5 w-5 text-white"
                aria-hidden="true"
              />
              <span>Add</span>
            </>
          )}
        </button>
        <button
          type="button"
          onClick={handleClose}
          className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
        >
          Cancel
        </button>
      </div>
    </ModalFrame>
  );
};

export default AddTailAlertModal;
